/**
 * Geocoding service using Nominatim (OpenStreetMap)
 * Rate limited to 1 request per second as per Nominatim usage policy
 * https://operations.osmfoundation.org/policies/nominatim/
 */

export interface GeocodingResult {
  placeId: string;
  displayName: string;
  lat: number;
  lon: number;
  type: string;
  addressComponents: {
    road?: string;
    houseNumber?: string;
    neighbourhood?: string;
    suburb?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
    countryCode?: string;
  };
  boundingBox?: [number, number, number, number]; // [south, north, west, east]
}

export interface ReverseGeocodingResult extends GeocodingResult {}

// Rate limiter - ensures minimum 1 second between requests
class RateLimiter {
  private lastRequestTime = 0;
  private readonly minInterval: number;

  constructor(requestsPerSecond: number = 1) {
    this.minInterval = 1000 / requestsPerSecond;
  }

  async waitForNextSlot(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.minInterval) {
      const waitTime = this.minInterval - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    this.lastRequestTime = Date.now();
  }
}

const rateLimiter = new RateLimiter(1); // 1 request per second

const NOMINATIM_BASE_URL = "https://nominatim.openstreetmap.org";

// User-Agent required by Nominatim
const USER_AGENT = "Rango/1.0 (https://rango.cloud)";

/**
 * Search for addresses/places
 * @param query - Search query string
 * @param options - Search options
 */
export async function searchAddress(
  query: string,
  options: {
    limit?: number;
    countryCode?: string; // ISO 3166-1 alpha-2 country code (e.g., 'br' for Brazil)
    bounded?: boolean;
    viewbox?: [number, number, number, number]; // [west, south, east, north]
  } = {}
): Promise<GeocodingResult[]> {
  if (!query || query.trim().length < 3) {
    return [];
  }

  await rateLimiter.waitForNextSlot();

  const params = new URLSearchParams({
    q: query,
    format: "jsonv2",
    addressdetails: "1",
    limit: String(options.limit || 5),
  });

  // Default to Brazil for better results
  if (options.countryCode) {
    params.set("countrycodes", options.countryCode);
  }

  if (options.bounded && options.viewbox) {
    params.set("bounded", "1");
    params.set("viewbox", options.viewbox.join(","));
  }

  try {
    const response = await fetch(`${NOMINATIM_BASE_URL}/search?${params}`, {
      headers: {
        "User-Agent": USER_AGENT,
        "Accept": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const data = await response.json();
    return data.map(parseNominatimResult);
  } catch (error) {
    console.error("Geocoding error:", error);
    return [];
  }
}

/**
 * Reverse geocode coordinates to address
 * @param lat - Latitude
 * @param lon - Longitude
 */
export async function reverseGeocode(
  lat: number,
  lon: number
): Promise<ReverseGeocodingResult | null> {
  await rateLimiter.waitForNextSlot();

  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    format: "jsonv2",
    addressdetails: "1",
  });

  try {
    const response = await fetch(`${NOMINATIM_BASE_URL}/reverse?${params}`, {
      headers: {
        "User-Agent": USER_AGENT,
        "Accept": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Nominatim API error: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.error) {
      return null;
    }

    return parseNominatimResult(data);
  } catch (error) {
    console.error("Reverse geocoding error:", error);
    return null;
  }
}

// Parse Nominatim response to our format
function parseNominatimResult(result: any): GeocodingResult {
  const address = result.address || {};
  
  return {
    placeId: String(result.place_id),
    displayName: result.display_name,
    lat: parseFloat(result.lat),
    lon: parseFloat(result.lon),
    type: result.type || result.category || "unknown",
    addressComponents: {
      road: address.road,
      houseNumber: address.house_number,
      neighbourhood: address.neighbourhood || address.suburb,
      suburb: address.suburb,
      city: address.city || address.town || address.village || address.municipality,
      state: address.state,
      postcode: address.postcode,
      country: address.country,
      countryCode: address.country_code,
    },
    boundingBox: result.boundingbox
      ? result.boundingbox.map((v: string) => parseFloat(v))
      : undefined,
  };
}

/**
 * Format address for display (shorter version)
 */
export function formatShortAddress(result: GeocodingResult): string {
  const { addressComponents } = result;
  const parts: string[] = [];

  if (addressComponents.road) {
    let street = addressComponents.road;
    if (addressComponents.houseNumber) {
      street += `, ${addressComponents.houseNumber}`;
    }
    parts.push(street);
  }

  if (addressComponents.neighbourhood) {
    parts.push(addressComponents.neighbourhood);
  } else if (addressComponents.suburb) {
    parts.push(addressComponents.suburb);
  }

  if (addressComponents.city) {
    parts.push(addressComponents.city);
  }

  return parts.join(" - ") || result.displayName;
}

/**
 * Get user's current location using browser geolocation API
 */
export function getCurrentLocation(): Promise<{ lat: number; lon: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation não suportada pelo navegador"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
      },
      (error) => {
        let message = "Erro ao obter localização";
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = "Permissão de localização negada";
            break;
          case error.POSITION_UNAVAILABLE:
            message = "Localização indisponível";
            break;
          case error.TIMEOUT:
            message = "Tempo esgotado ao obter localização";
            break;
        }
        reject(new Error(message));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes cache
      }
    );
  });
}

